/**
 * Which service line a client belongs to (Tali, 2026-10-14: filter Plans by
 * service, as Chase does).
 *
 * The same walk omon-api's Chase does (routes/chase.py `_client_services`):
 * the client's episode names an area team, and the team is `partOf` an
 * organisation typed `service-line` (Dementia, Brain Injury, …). Walked,
 * never parsed out of a name. The whole `_organization` collection is small
 * (37 docs, 2026-10-14) and readable by any signed-in user under both
 * rulesets, so it is read once per session and walked in memory.
 */
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export const SERVICE_LINE_CODE = "service-line";
const MAX_HOPS = 4;

export interface OrgNode {
  name: string | null;
  partOfId: string | null;
  isServiceLine: boolean;
}

/** Firestore stores some arrays as numeric-keyed maps; read both. */
function asList(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return Object.values(v as Record<string, unknown>);
  return [];
}

export function orgNodeFrom(data: Record<string, unknown>): OrgNode {
  const codes = asList(data.type).flatMap((t) =>
    asList((t as { coding?: unknown })?.coding).map((c) => (c as { code?: unknown })?.code),
  );
  const parent = (data.partOf as { reference?: { id?: unknown } } | undefined)?.reference?.id;
  return {
    name: typeof data.name === "string" ? data.name : null,
    partOfId: typeof parent === "string" ? parent : null,
    isServiceLine: codes.includes(SERVICE_LINE_CODE),
  };
}

/** The service-line name above a team, or null when the chain breaks. */
export function serviceOfTeam(teamId: string | null | undefined, orgs: Map<string, OrgNode>): string | null {
  let id = teamId ?? null;
  for (let hop = 0; id && hop < MAX_HOPS; hop++) {
    const node = orgs.get(id);
    if (!node) return null;
    if (node.isServiceLine) return node.name;
    id = node.partOfId;
  }
  return null;
}

let index: Promise<Map<string, OrgNode>> | null = null;

/** Every organisation, read once per session (a failure retries next time). */
export function loadOrgIndex(): Promise<Map<string, OrgNode>> {
  if (!index) {
    index = getDocs(collection(db, "_organization"))
      .then((snap) => {
        const m = new Map<string, OrgNode>();
        snap.forEach((d) => m.set(d.id, orgNodeFrom(d.data())));
        return m;
      })
      .catch((err) => {
        index = null;
        throw err;
      });
  }
  return index;
}
